import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Stack } from '@mui/material';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { ScreenComponent, ScreenConfig } from 'new/routes/RouterManager';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import { ROUTE_ACCESS_REQUESTS } from 'new/constants/pathnames';
import { BannerDescription } from 'new/elements/BannerDescription';
import { Tabs } from 'new/elements/Tabs';
import { TabProps } from 'new/elements/Tabs/Tabs';
import styles from './AccessRequests.module.scss';
import { RequestsTable } from '../components/RequestsTable/RequestsTable';
import { loadIncomingAccessRequests, loadOutgoingAccessRequests } from 'js/actions';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import {
  GET_INCOMING_ACCESS_REQUESTS_REQUEST,
  GET_OUTGOING_ACCESS_REQUESTS_REQUEST,
} from 'js/constants/actionTypes';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { useMarketplaceRequests } from 'new/hooks/adapters/useMarketplaceRequests';

interface AccessRequestsProps {}

const description = 'Respond to requests to use Data Products from the Marketplace.';

const tabs = [
  {
    value: 'incoming',
    label: 'Incoming',
  },
  {
    value: 'outgoing',
    label: 'Outgoing',
  },
] as TabProps[];

export const AccessRequests: ScreenComponent<AccessRequestsProps> = (
  props: AccessRequestsProps,
) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const user = useCurrentUser();
  const isCustodian = user.isOrgCustodian || user.isDomainCustodian;
  const isLoading = useIsLoading(
    GET_OUTGOING_ACCESS_REQUESTS_REQUEST,
    GET_INCOMING_ACCESS_REQUESTS_REQUEST,
  );
  const { outgoingAccessRequests: outgoingItems, incomingAccessRequests } =
    useMarketplaceRequests();
  const incomingItems = isCustodian ? incomingAccessRequests : [];
  const visibleTabs = isCustodian ? tabs : tabs.filter((tab) => tab.value === 'outgoing');
  const [selectedTab, setSelectedTab] = useState(visibleTabs[0]);

  useEffect(() => {
    dispatch(loadOutgoingAccessRequests());
    if (isCustodian) {
      dispatch(loadIncomingAccessRequests());
    }
  }, []);

  const onTabChange = (e, value) => {
    setSelectedTab(tabs.find((tab) => tab.value === value));
  };

  return (
    <>
      <BannerDescription title="Access Requests" description={description} />
      <Stack className={styles.tabsContainer}>
        <Tabs value={selectedTab?.value} tabs={visibleTabs} onChange={onTabChange} />
      </Stack>
      <RequestsTable
        selectedTab={selectedTab?.value}
        requestType="access"
        loading={isLoading}
        incomingItems={incomingItems}
        outgoingItems={outgoingItems}
      />
    </>
  );
};

AccessRequests.screenConfig = {
  path: `${ROUTE_REDESIGN}${ROUTE_ACCESS_REQUESTS}`,
  domain: 'marketplace',
  header: {
    title: 'Access Requests',
    slugTitle: 'Access Requests',
  },
  screenName: 'AccessRequests',
} as ScreenConfig<AccessRequestsProps>;
